/**
 * Support widget UI state — Zustand, mirrors uiStore's persist pattern.
 *
 * Only `draft` survives a reload (sessionStorage, tab-scoped — NOT
 * localStorage) so an in-progress compose message isn't lost on an
 * accidental tab close. Open/view/tab state is intentionally NOT persisted —
 * the widget always starts closed on Home.
 *
 * PHI note: the draft is free text a user may paste client information into.
 * sessionStorage alone does NOT clear it on logout-then-login in the same
 * tab (it's scoped to the tab, not the session) — `authStore.clearAuth()`
 * and `beginLogout()` explicitly remove the `support-widget-storage` key
 * from both sessionStorage and localStorage as the load-bearing half of this
 * fix. See the identity-boundary comment there.
 */

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { LANE_SUPPORT_TYPES, defaultSupportTypeForLane } from '@upskill/shared';
import type { SupportLane, SupportRequestType } from '@upskill/shared';

/** Every currently-valid type, for validating a rehydrated draft (see `migrate`). */
const VALID_SUPPORT_TYPES = new Set<SupportRequestType>([
  ...LANE_SUPPORT_TYPES.ACCOUNT,
  ...LANE_SUPPORT_TYPES.PLATFORM,
]);

export type SupportWidgetTab = 'home' | 'messages' | 'help';
// 'article' was removed — help articles now navigate to a full page
// (/help/:articleId) instead of rendering in-panel.
export type SupportWidgetView = 'home' | 'messages' | 'compose' | 'thread' | 'help';

export interface SupportComposePrefill {
  lane?: SupportLane;
  type?: SupportRequestType;
  subject?: string;
  errorRef?: string;
}

export interface SupportDraft {
  /**
   * `null` until the user explicitly picks a lane. Must NOT default to
   * 'PLATFORM': an untouched segmented control would silently route
   * org-internal detail (names, caseload/staffing info) to the external
   * UpskillABA vendor inbox instead of the requester's own Org Admin.
   * ComposeView blocks submit while this is null.
   */
  lane: SupportLane | null;
  type: SupportRequestType;
  subject: string;
  body: string;
}

const EMPTY_DRAFT: SupportDraft = {
  lane: null,
  type: 'PLATFORM_QUESTION',
  subject: '',
  body: '',
};

/** SR-<reference> + subject for the open thread's panel header (ADO #542 items
 * 9/10) — lifted here from ThreadView's own fetch rather than re-fetched by
 * SupportWidget, so there's exactly one request for the thread's detail. */
export interface SupportThreadHeader {
  subject: string;
  reference: number;
}

interface SupportWidgetState {
  isOpen: boolean;
  /**
   * `view` doubles as "which tab is active" whenever it's one of the three
   * top-level tabs — SupportPanel only shows the tab bar in that case, so a
   * separate `activeTab` field would just be `view` copied into a second
   * place that could drift out of sync with it.
   */
  view: SupportWidgetView;
  threadId: string | null;
  /** Set by openReportIssue(); ComposeView applies it once then the caller clears it. */
  composePrefill: SupportComposePrefill | null;
  draft: SupportDraft;
  /** Null until ThreadView's detail query resolves for the current thread —
   * SupportWidget falls back to "Conversation" while this is null. */
  threadHeader: SupportThreadHeader | null;
}

interface SupportWidgetActions {
  openWidget: () => void;
  closeWidget: () => void;
  toggleWidget: () => void;
  setActiveTab: (tab: SupportWidgetTab) => void;
  goToCompose: (prefill?: SupportComposePrefill) => void;
  openThread: (threadId: string) => void;
  goHome: () => void;
  /** Global entry point for "Report this issue" hooks — opens the widget straight to compose. */
  openReportIssue: (prefill?: SupportComposePrefill) => void;
  clearComposePrefill: () => void;
  setDraft: (partial: Partial<SupportDraft>) => void;
  clearDraft: () => void;
  setThreadHeader: (header: SupportThreadHeader) => void;
}

type SupportWidgetStore = SupportWidgetState & SupportWidgetActions;

export const useSupportWidgetStore = create<SupportWidgetStore>()(
  persist(
    (set) => ({
      isOpen: false,
      view: 'home',
      threadId: null,
      composePrefill: null,
      draft: EMPTY_DRAFT,
      threadHeader: null,

      openWidget: () => set({ isOpen: true }),
      closeWidget: () => set({ isOpen: false }),
      toggleWidget: () => set((s) => ({ isOpen: !s.isOpen })),

      setActiveTab: (tab) => set({ view: tab, composePrefill: null, threadHeader: null }),

      goToCompose: (prefill) =>
        set({ view: 'compose', composePrefill: prefill ?? null }),

      // Both are also the target of notification deep links (bell click,
      // /?support=<id>), which can arrive while the panel is closed — so
      // both must open it themselves rather than assume a caller will.
      // threadHeader resets so a previous thread's subject never flashes
      // before this one's detail query resolves.
      openThread: (threadId) => set({ isOpen: true, view: 'thread', threadId, threadHeader: null }),

      goHome: () => set({ view: 'home', composePrefill: null, threadHeader: null }),

      openReportIssue: (prefill) =>
        set({
          isOpen: true,
          view: 'compose',
          composePrefill: { lane: 'PLATFORM', type: 'PLATFORM_BUG', ...prefill },
        }),

      clearComposePrefill: () => set({ composePrefill: null }),

      setDraft: (partial) => set((s) => ({ draft: { ...s.draft, ...partial } })),
      clearDraft: () => set({ draft: EMPTY_DRAFT }),
      setThreadHeader: (header) => set({ threadHeader: header }),
    }),
    {
      name: 'support-widget-storage',
      // Tab-scoped, not device/browser-scoped — see the PHI note above. This
      // is defense-in-depth on top of the explicit removeItem calls in
      // authStore; it is not sufficient on its own (same-tab logout/login
      // keeps sessionStorage).
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ draft: state.draft }),
      // Bumped to 2 for the lane-partitioned type rename (ADO #542 item 3):
      // a draft persisted before this ships carries a flat legacy value
      // ('QUESTION', 'BUG', 'ACCOUNT_ACCESS'…) that is no longer a valid
      // SupportRequestType, which would hydrate an option the Select cannot
      // show and the API would reject on submit.
      version: 2,
      migrate: (persisted, fromVersion) => {
        const state = persisted as { draft?: SupportDraft } | undefined;
        if (!state?.draft || fromVersion >= 2) return state;
        // Remap the four legacy values that have a direct successor, and fall
        // back to the catch-all for anything else. The user's typed subject and
        // body are the expensive part of a draft — keep them and correct only
        // the type, rather than discarding what they were in the middle of
        // writing.
        const LEGACY_TYPE: Record<string, SupportRequestType> = {
          BUG: 'PLATFORM_BUG',
          QUESTION: 'PLATFORM_QUESTION',
          SUGGESTION: 'PLATFORM_FEATURE_REQUEST',
          BILLING: 'PLATFORM_BILLING',
          ACCOUNT_ACCESS: 'ACCOUNT_SIGN_IN_ACCESS',
        };
        const current = state.draft.type as string;
        const mapped =
          LEGACY_TYPE[current] ??
          (VALID_SUPPORT_TYPES.has(current as SupportRequestType)
            ? (current as SupportRequestType)
            : defaultSupportTypeForLane(state.draft.lane ?? 'PLATFORM'));
        return { ...state, draft: { ...state.draft, type: mapped } };
      },
    },
  ),
);
